import React, { useState } from 'react';
import { ChevronRight, MapPin } from 'lucide-react';
type RegionData = {
  departments: string[];
  cities: string[];
};
const regionsData: Record<string, RegionData> = {
  'Île-de-France': {
    departments: ['75 - Paris', '77 - Seine-et-Marne', '78 - Yvelines', '91 - Essonne', '92 - Hauts-de-Seine', '93 - Seine-Saint-Denis', '94 - Val-de-Marne', "95 - Val-d'Oise"],
    cities: ['Paris', 'Boulogne-Billancourt', 'Saint-Denis', 'Argenteuil', 'Montreuil', 'Nanterre', 'Créteil', 'Versailles', 'Courbevoie']
  },
  'Auvergne-Rhône-Alpes': {
    departments: ['69 - Rhône', '38 - Isère', '63 - Puy-de-Dôme', '01 - Ain', '42 - Loire', '73 - Savoie', '74 - Haute-Savoie', '03 - Allier'],
    cities: ['Lyon', 'Grenoble', 'Clermont-Ferrand', 'Saint-Étienne', 'Villeurbanne', 'Annecy', 'Chambéry', 'Valence', 'Bourg-en-Bresse']
  },
  'Nouvelle-Aquitaine': {
    departments: ['33 - Gironde', '64 - Pyrénées-Atlantiques', '40 - Landes', '47 - Lot-et-Garonne', '24 - Dordogne', '17 - Charente-Maritime', '79 - Deux-Sèvres', '86 - Vienne'],
    cities: ['Bordeaux', 'Pau', 'La Rochelle', 'Limoges', 'Bayonne', 'Périgueux', 'Angoulême', 'Niort', 'Agen']
  },
  Occitanie: {
    departments: ['31 - Haute-Garonne', '34 - Hérault', '81 - Tarn', '82 - Tarn-et-Garonne', '66 - Pyrénées-Orientales', '11 - Aude', '30 - Gard', '48 - Lozère'],
    cities: ['Toulouse', 'Montpellier', 'Nîmes', 'Perpignan', 'Béziers', 'Narbonne', 'Albi', 'Carcassonne', 'Sète']
  },
  'Hauts-de-France': {
    departments: ['59 - Nord', '62 - Pas-de-Calais', '80 - Somme', '02 - Aisne', '60 - Oise'],
    cities: ['Lille', 'Amiens', 'Roubaix', 'Tourcoing', 'Dunkerque', 'Calais', 'Valenciennes', 'Boulogne-sur-Mer', 'Saint-Quentin']
  },
  "Provence-Alpes-Côte d'Azur": {
    departments: ['13 - Bouches-du-Rhône', '06 - Alpes-Maritimes', '83 - Var', '84 - Vaucluse', '04 - Alpes-de-Haute-Provence', '05 - Hautes-Alpes'],
    cities: ['Marseille', 'Nice', 'Toulon', 'Aix-en-Provence', 'Avignon', 'Cannes', 'Antibes', 'La Seyne-sur-Mer', 'Hyères']
  },
  'Grand Est': {
    departments: ['67 - Bas-Rhin', '68 - Haut-Rhin', '57 - Moselle', '54 - Meurthe-et-Moselle', '88 - Vosges', '51 - Marne', '55 - Meuse', '08 - Ardennes'],
    cities: ['Strasbourg', 'Reims', 'Metz', 'Mulhouse', 'Nancy', 'Colmar', 'Troyes', 'Charleville-Mézières', 'Châlons-en-Champagne']
  },
  'Pays de la Loire': {
    departments: ['44 - Loire-Atlantique', '49 - Maine-et-Loire', '85 - Vendée', '72 - Sarthe', '53 - Mayenne'],
    cities: ['Nantes', 'Angers', 'Le Mans', 'Saint-Nazaire', 'La Roche-sur-Yon', 'Cholet', 'Laval', "Les Sables-d'Olonne", 'Saint-Herblain']
  },
  Normandie: {
    departments: ['76 - Seine-Maritime', '14 - Calvados', '27 - Eure', '50 - Manche', '61 - Orne'],
    cities: ['Rouen', 'Le Havre', 'Caen', 'Cherbourg', 'Évreux', 'Dieppe', 'Saint-Étienne-du-Rouvray', 'Alençon', 'Lisieux']
  },
  Bretagne: {
    departments: ['35 - Ille-et-Vilaine', '29 - Finistère', '56 - Morbihan', "22 - Côtes-d'Armor"],
    cities: ['Rennes', 'Brest', 'Quimper', 'Lorient', 'Vannes', 'Saint-Malo', 'Saint-Brieuc', 'Lanester', 'Fougères']
  }
};
export function InterventionZones() {
  const [selectedRegion, setSelectedRegion] = useState<string>('Île-de-France');
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const handleRegionClick = (region: string) => {
    setSelectedRegion(region);
    setSelectedDepartment(null);
  };
  return <section id="zones" className="py-24 bg-gradient-to-b from-white to-gray-50" data-id="element-180">
      <div className="w-full max-w-[1200px] mx-auto px-6" data-id="element-181">
        <div className="text-center mb-16" data-id="element-182">
          <span className="text-primary font-semibold tracking-wider uppercase text-sm" data-id="element-183">
            Couverture nationale
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-dark mt-3 mb-6" data-id="element-184">
            Nous livrons partout en France
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-185">
            Un maillage logistique dense pour garantir réactivité et proximité.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-186">
          {/* Region Selector */}
          <div className="lg:col-span-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-[500px] flex flex-col" data-id="element-187">
            <div className="p-6 border-b border-gray-100 bg-gray-50/50" data-id="element-188">
              <h3 className="text-lg font-bold text-secondary-dark flex items-center gap-2" data-id="element-189">
                <MapPin size={20} className="text-primary" data-id="element-190" />
                Sélectionnez votre région
              </h3>
            </div>
            <div className="overflow-y-auto flex-1 p-2 custom-scrollbar" data-id="element-191">
              <ul className="space-y-1" data-id="element-192">
                {Object.keys(regionsData).map(region => <li key={region} onClick={() => handleRegionClick(region)} className={`
                      flex justify-between items-center p-3 rounded-xl cursor-pointer transition-all duration-200
                      ${selectedRegion === region ? 'bg-primary text-white shadow-md shadow-primary/20' : 'hover:bg-gray-50 text-gray-600'}
                    `} data-id="element-193">
                    <span className="font-medium text-sm" data-id="element-194">{region}</span>
                    <ChevronRight size={16} className={selectedRegion === region ? 'text-white' : 'text-gray-300'} data-id="element-195" />
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Details Panel */}
          <div className="lg:col-span-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-8 flex flex-col h-[500px]" data-id="element-196">
            <div className="mb-6" data-id="element-197">
              <h3 className="text-2xl font-bold text-secondary-dark mb-2" data-id="element-198">
                {selectedRegion}
              </h3>
              <p className="text-gray-500" data-id="element-199">
                Livraison garantie sous 48 à 72h dans cette région.
              </p>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col" data-id="element-200">
              <div className="mb-6" data-id="element-201">
                <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-202">
                  Départements desservis
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 overflow-y-auto max-h-[160px] pr-2 custom-scrollbar" data-id="element-203">
                  {regionsData[selectedRegion].departments.map(dept => <div key={dept} onClick={() => setSelectedDepartment(dept)} className={`
                        p-2.5 rounded-lg text-center text-xs font-medium cursor-pointer transition-all border
                        ${selectedDepartment === dept ? 'bg-secondary-dark text-white border-secondary-dark shadow-md' : 'bg-gray-50 text-gray-600 border-gray-100 hover:border-primary hover:text-primary'}
                      `} data-id="element-204">
                      {dept}
                    </div>)}
                </div>
              </div>

              {selectedDepartment && <div className="flex-1 border-t border-gray-100 pt-6 animate-fade-in" data-id="element-205">
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-206">
                    Principales villes dans le{' '}
                    {selectedDepartment.split(' - ')[0]}
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-207">
                    {regionsData[selectedRegion].cities.map(city => <span key={city} className="px-3 py-1.5 bg-primary-light/30 text-primary-dark rounded-full text-xs font-medium" data-id="element-208">
                        {city}
                      </span>)}
                    <span className="px-3 py-1.5 bg-gray-50 text-gray-400 rounded-full text-xs font-medium" data-id="element-209">
                      + autres communes
                    </span>
                  </div>
                </div>}

              {!selectedDepartment && <div className="flex-1 border-t border-gray-100 pt-6 flex items-center justify-center text-gray-400 text-sm italic" data-id="element-210">
                  Sélectionnez un département pour voir les villes
                </div>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}